import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  GraduationCap,
  ShoppingBag,
  Layers,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Home,
  Bot as SmartToy,
} from "lucide-react";
import AISidebar from "./AISidebar";
import { KRUMAGO } from "../../s3-assets/Krumago";

// ─── Domain config ─────────────────────────────────────────────────────────────
// Add a new entry here whenever a new domain/industry is added.
// prefixes : every pathname that belongs to this domain
// home     : where "Home" navigates when inside this domain
// about    : where "About" navigates when inside this domain
const DOMAINS = [
  {
    prefixes: ['/cowork-management-software', '/flexoffice', '/about-flexoffice'],
    home: '/cowork-management-software',
    about: '/about-flexoffice',
    label: 'FlexOffice',
  },
  {
    prefixes: ['/academy-management-software', '/academy', '/about-academy'],
    home: '/academy-management-software',
    about: '/about-academy',
    label: 'Academy',
  },
  {
    prefixes: ['/retail-management-software', '/retail', '/about-retail'],
    home: '/retail-management-software',
    about: '/about-retail',
    label: 'Retail',
  },
  {
    prefixes: ['/waas-management-software', '/waas', '/about-waas'],
    home: '/waas-management-software',
    about: '/about-waas',
    label: 'WaaS',
  },
];

// Pages that are "shared" — they belong to whichever domain the user came from,
// but fall back to the Krumago root if there's no remembered domain.
const SHARED_PAGES = ['/contact'];

const ALWAYS_WHITE_PAGES = [
  '/who-are-we', '/contact', '/features',
  '/about-flexoffice', '/about-academy', '/about-retail', '/about-waas', '/fit-test',
];

const industries = [
  {
    icon: <Building2 className="w-5 h-5" />,
    label: "FlexOffice",
    description: "Coworking & flex space management",
    gradient: "from-blue-500 to-cyan-500",
    href: "/cowork-management-software",
  },
  {
    icon: <GraduationCap className="w-5 h-5" />,
    label: "Academy",
    description: "Education & course delivery",
    gradient: "from-purple-500 to-pink-500",
    href: "/academy-management-software",
  },
  {
    icon: <ShoppingBag className="w-5 h-5" />,
    label: "Retail",
    description: "Store & inventory management",
    gradient: "from-green-500 to-emerald-500",
    href: "/retail-management-software",
  },
  {
    icon: <Layers className="w-5 h-5" />,
    label: "WaaS",
    description: "Workspace as a Service platform",
    gradient: "from-orange-500 to-rose-500",
    href: "/waas-management-software",
  },
];

// ─── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Returns the active domain strictly based on the current pathname
 * (ignoring shared pages like /contact).
 */
function useDomainFromPath(pathname) {
  return useMemo(
    () => DOMAINS.find((d) => d.prefixes.some((p) => pathname.startsWith(p))) ?? null,
    [pathname]
  );
}

/**
 * Returns the "contextual" domain for the current page:
 * - If the current page directly belongs to a domain → that domain.
 * - If the current page is a shared page (e.g. /contact) → the last remembered
 *   domain stored in sessionStorage (set whenever the user visits a real domain page).
 * - Otherwise → null (Krumago root context).
 */
function useActiveDomain(pathname) {
  const domainFromPath = useDomainFromPath(pathname);
  const isSharedPage = SHARED_PAGES.some((p) => pathname.startsWith(p));
  // A "root" page is anything that is neither a domain page nor a shared page
  // e.g. /, /who-are-we, /features — visiting these clears any stored domain.
  const isKrumagoRootPage = !domainFromPath && !isSharedPage;

  useEffect(() => {
    if (domainFromPath) {
      // On a real domain page — remember it.
      sessionStorage.setItem('krumago_last_domain', JSON.stringify(domainFromPath));
    } else if (isKrumagoRootPage) {
      // On a Krumago root page — wipe stale domain so /contact stays clean.
      sessionStorage.removeItem('krumago_last_domain');
    }
    // On shared pages (/contact) — do nothing, preserve whatever is stored.
  }, [domainFromPath, isKrumagoRootPage]);

  return useMemo(() => {
    if (domainFromPath) return domainFromPath;

    if (isSharedPage) {
      try {
        const stored = sessionStorage.getItem('krumago_last_domain');
        if (stored) return JSON.parse(stored);
      } catch {
        // ignore parse errors
      }
    }

    return null;
  }, [domainFromPath, isSharedPage]);
}

// ─── Component ─────────────────────────────────────────────────────────────────
const KrumagoHeader = () => {
  const nav = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const activeDomain = useActiveDomain(location.pathname);
  const domainFromPath = useDomainFromPath(location.pathname);

  // Industries dropdown is hidden ONLY when the current path directly belongs to
  // a domain (e.g. /cowork-management-software, /about-flexoffice).
  // Shared pages like /contact always show Industries regardless of inherited domain
  // context — they are Krumago-level pages, not domain pages.
  const isIndustryPage = domainFromPath !== null;

  const isAlwaysWhitePage = ALWAYS_WHITE_PAGES.some((p) => location.pathname.startsWith(p));

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const useWhiteStyle = isScrolled || isAlwaysWhitePage;
  const memoizedIndustries = useMemo(() => industries, []);

  const isIndustryActive = useMemo(
    () => memoizedIndustries.some((ind) => location.pathname === ind.href),
    [location.pathname, memoizedIndustries]
  );

  // ── Derived nav targets ────────────────────────────────────────────────────
  // homeHref  : "Home" link + logo-domain-tag click target
  // aboutHref : "About" link target
  // showKrumagoLink : show the domain pill only when inside a domain
  //                   (not on shared pages — contact is already its own page)
  const homeHref = activeDomain ? activeDomain.home : '/';
  const aboutHref = activeDomain ? activeDomain.about : '/who-are-we';

  // Show the domain label pill on real domain pages AND on shared pages that
  // have inherited a domain context, so the user always knows which product
  // they're interacting with.
  const showKrumagoLink = activeDomain !== null;

  // On shared pages, "Back to Krumago" in mobile should still appear if we have
  // a domain context (to let the user escape back to root if needed).
  // On real domain pages this was already shown.
  const showBackToKrumago = activeDomain !== null;

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1024) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileIndustriesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setMobileIndustriesOpen(false);
        setDropdownOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener("keydown", onEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (dropdownOpen) {
      setShowDropdown(true);
    } else {
      const t = setTimeout(() => setShowDropdown(false), 200);
      return () => clearTimeout(t);
    }
  }, [dropdownOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAIClick = () => setIsAISidebarOpen(true);
  const handleAIClose = () => setIsAISidebarOpen(false);

  const handleDropdownKeyDown = useCallback((e) => {
    if (e.key === "Escape") setDropdownOpen(false);
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setDropdownOpen((p) => !p);
    }
  }, []);

  const handleIndustryClick = useCallback((href) => {
    nav(href);
    setDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [nav]);

  const handleMobileNavClick = useCallback((path) => {
    nav(path);
    setIsMobileMenuOpen(false);
    setMobileIndustriesOpen(false);
  }, [nav]);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // ── Shared class helpers ──────────────────────────────────────────────────
  const navLinkCls = `font-semibold px-2 xl:px-3 py-1 transition-colors duration-300 text-xs xl:text-sm ${
    useWhiteStyle ? 'text-slate-700' : 'text-white'
  }`;

  const mobileNavBtnCls = `font-semibold py-2 px-3 md:px-4 rounded-xl transition-all duration-200 text-sm md:text-base text-left w-full ${
    useWhiteStyle ? 'text-slate-700 hover:bg-slate-50' : 'text-white hover:bg-white/10'
  }`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <header className={`
        fixed safe-area-top left-0 right-0 z-50 transition-all duration-300
        ${useWhiteStyle ? 'bg-white shadow-md py-1' : 'bg-transparent py-2 md:py-4'}
      `}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`
            flex justify-between items-center transition-all duration-300
            ${useWhiteStyle ? 'h-10 md:h-12 lg:h-14' : 'h-14 md:h-16 lg:h-20'}
          `}>

            {/* ── Left: Split logo ──────────────────────────────────────── */}
            <div className="flex-shrink-0 flex items-center">

              {/* Logo → always Krumago root */}
              <Link to="/" className="flex items-center">
                <img
                  loading="lazy"
                  src={KRUMAGO.IMAGES.KRUMAGO_3}
                  width={useWhiteStyle
                    ? (isMobile ? "90px" : isTablet ? "100px" : "120px")
                    : (isMobile ? "110px" : isTablet ? "130px" : "150px")}
                  height={useWhiteStyle
                    ? (isMobile ? "30px" : isTablet ? "35px" : "40px")
                    : (isMobile ? "40px" : isTablet ? "45px" : "50px")}
                  alt="Krumago"
                  className="object-contain transition-all duration-300"
                />
              </Link>

              {/* Domain label pill → navigates to that domain's home */}
              {showKrumagoLink && (
                <Link
                  to={activeDomain?.home}
                  className={`
                    inline-flex items-center ml-2 px-3 py-1.5
                    text-[11px] font-semibold rounded-lg border transition-all duration-300
                    ${useWhiteStyle
                      ? 'text-slate-400 border-slate-200 hover:text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      : 'text-white/40 border-white/10 hover:text-white/70 hover:border-white/20 hover:bg-white/5'}
                  `}
                >
                  {activeDomain?.label}
                </Link>
              )}
            </div>

            {/* ── Desktop nav ──────────────────────────────────────────────── */}
            <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">

              {/* Home → domain home or "/" */}
              <Link to={homeHref} className={navLinkCls}>
                Home
              </Link>

              {/* Industries dropdown — hidden when inside a domain OR on a shared
                  page that has inherited a domain context */}
              {!isIndustryPage && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((p) => !p)}
                    onKeyDown={handleDropdownKeyDown}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                    className={`inline-flex items-center gap-1.5 font-semibold px-2 xl:px-3 py-1 transition-colors duration-300 text-xs xl:text-sm
                      ${useWhiteStyle ? 'text-slate-700' : 'text-white'}
                      ${dropdownOpen || isIndustryActive ? (useWhiteStyle ? 'text-blue-600' : 'text-blue-300') : ''}
                    `}
                  >
                    Industries
                    <ChevronDown className={`w-3 h-3 xl:w-4 xl:h-4 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {dropdownOpen && showDropdown && (
                    <div
                      id="industries-dropdown"
                      role="menu"
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 xl:w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50"
                    >
                      <div className="px-4 xl:px-5 pt-4 pb-2 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-400 tracking-widest">Industry Solutions</p>
                      </div>
                      <div className="p-2 xl:p-3 space-y-1">
                        {memoizedIndustries.map((ind, i) => (
                          <button
                            key={i}
                            onClick={() => handleIndustryClick(ind.href)}
                            role="menuitem"
                            className="group w-full flex items-center gap-3 xl:gap-4 px-2 xl:px-3 py-2 xl:py-3 rounded-xl hover:bg-slate-50 transition-all duration-200 text-left"
                          >
                            <div className={`w-8 h-8 xl:w-10 xl:h-10 bg-gradient-to-br ${ind.gradient} rounded-xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-all duration-300 shadow-md`}>
                              {ind.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-slate-800 text-xs xl:text-sm group-hover:text-blue-600">{ind.label}</div>
                              <div className="text-slate-500 text-xs mt-0.5 truncate">{ind.description}</div>
                            </div>
                            <ArrowRight className="w-3 h-3 xl:w-4 xl:h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all duration-200" />
                          </button>
                        ))}
                      </div>
                      <div className="px-4 xl:px-5 py-2 xl:py-3 bg-slate-50 border-t border-slate-100">
                        <p className="text-xs text-slate-400 font-medium text-center">More coming: Travel · Healthcare · Transport</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* About → domain about or "/who-are-we" */}
              <Link to={aboutHref} className={navLinkCls}>About</Link>

              {/* Use the visitor's desired outcome instead of an internal workflow name. */}
              <Link to="/fit-test" className={navLinkCls}>Discover Opportunities</Link>

              {/* Contact — always goes to /contact */}
              <Link to="/contact" className={navLinkCls}>Contact</Link>
            </nav>

            {/* ── Right: CTA buttons + hamburger ───────────────────────────── */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <button
                onClick={() => nav("/contact")}
                className="hidden lg:inline-flex items-center gap-1 xl:gap-2 px-3 xl:px-5 py-1.5 xl:py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs xl:text-sm font-bold rounded-lg xl:rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg"
                aria-label="Get Started"
              >
                <Sparkles className="w-3 h-3 xl:w-4 xl:h-4" />
                <span className="hidden xl:inline">Get Started</span>
                <span className="xl:hidden">Start</span>
              </button>

              {!isAISidebarOpen && (
                <button
                  onClick={handleAIClick}
                  className={`hidden lg:flex items-center space-x-1 xl:space-x-2 px-3 xl:px-4 py-1.5 xl:py-2 font-semibold rounded-lg border transition-colors duration-300 text-xs xl:text-sm ${
                    useWhiteStyle ? 'text-slate-700 border-slate-300' : 'text-white border-white'
                  }`}
                >
                  <SmartToy className="w-3 h-3 xl:w-4 xl:h-4" />
                  <span className="hidden xl:inline">AI Assistant</span>
                  <span className="xl:hidden">AI</span>
                </button>
              )}

              {/* Hamburger */}
              <button
                className={`lg:hidden p-1.5 md:p-2 transition-colors duration-300 ${useWhiteStyle ? 'text-slate-700' : 'text-white'}`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                <div className="w-4 h-4 md:w-5 md:h-5 flex flex-col justify-center space-y-1">
                  <span className={`block h-0.5 w-4 md:w-5 transition-all duration-300 ${useWhiteStyle ? 'bg-slate-700' : 'bg-white'} ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
                  <span className={`block h-0.5 w-4 md:w-5 transition-all duration-300 ${useWhiteStyle ? 'bg-slate-700' : 'bg-white'} ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                  <span className={`block h-0.5 w-4 md:w-5 transition-all duration-300 ${useWhiteStyle ? 'bg-slate-700' : 'bg-white'} ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
                </div>
              </button>
            </div>
          </div>

          {/* ── Mobile menu ─────────────────────────────────────────────────── */}
          {isMobileMenuOpen && (
            <div className={`lg:hidden py-3 md:py-4 transition-all duration-300 ${useWhiteStyle ? 'bg-white' : 'bg-black/90'}`}>
              <nav className="flex flex-col space-y-1 px-3 md:px-4">

                {/* Home */}
                <button onClick={() => handleMobileNavClick(homeHref)} className={mobileNavBtnCls}>
                  Home
                </button>

                {/* Industries — hidden when inside a domain OR on a shared page
                    that has inherited a domain context */}
                {!isIndustryPage && (
                  <div>
                    <button
                      onClick={() => setMobileIndustriesOpen((p) => !p)}
                      aria-expanded={mobileIndustriesOpen}
                      className={`w-full flex items-center justify-between px-3 md:px-4 py-2 font-semibold rounded-xl transition-all duration-200 text-sm md:text-base ${
                        mobileIndustriesOpen || isIndustryActive
                          ? useWhiteStyle ? 'text-blue-600 bg-blue-50' : 'text-blue-300 bg-white/10'
                          : useWhiteStyle ? 'text-slate-700 hover:bg-slate-50' : 'text-white hover:bg-white/10'
                      }`}
                    >
                      <span>Industries</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileIndustriesOpen ? "rotate-180" : ""}`} />
                    </button>

                    {mobileIndustriesOpen && (
                      <div className="ml-4 mt-1 space-y-1 pb-1">
                        {memoizedIndustries.map((ind, i) => (
                          <button
                            key={i}
                            onClick={() => handleMobileNavClick(ind.href)}
                            className={`group w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all duration-200 text-left ${
                              location.pathname === ind.href
                                ? useWhiteStyle ? 'bg-blue-50' : 'bg-white/10'
                                : useWhiteStyle ? 'hover:bg-slate-50' : 'hover:bg-white/10'
                            }`}
                          >
                            <div className={`w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br ${ind.gradient} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                              {ind.icon}
                            </div>
                            <div>
                              <div className={`font-bold text-xs md:text-sm ${
                                location.pathname === ind.href
                                  ? (useWhiteStyle ? 'text-blue-600' : 'text-blue-300')
                                  : (useWhiteStyle ? 'text-slate-800' : 'text-white')
                              }`}>{ind.label}</div>
                              <div className={`text-xs ${useWhiteStyle ? 'text-slate-500' : 'text-white/70'}`}>{ind.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* About */}
                <button onClick={() => handleMobileNavClick(aboutHref)} className={mobileNavBtnCls}>
                  About
                </button>

                {/* Keep the mobile label aligned with the desktop navigation. */}
                <button onClick={() => handleMobileNavClick('/fit-test')} className={mobileNavBtnCls}>
                  Discover Opportunities
                </button>

                {/* Contact */}
                <button onClick={() => handleMobileNavClick('/contact')} className={mobileNavBtnCls}>
                  Contact
                </button>

                {/* Back to Krumago — shown on domain pages AND shared pages with
                    an inherited domain (so the user can always escape to root) */}
                {showBackToKrumago && (
                  <button
                    onClick={() => handleMobileNavClick('/')}
                    className={`w-full flex items-center gap-2 py-2 px-3 md:px-4 rounded-xl transition-all duration-200 text-sm md:text-base text-left border-t mt-1 pt-3 font-semibold ${
                      useWhiteStyle
                        ? 'text-slate-400 hover:text-slate-700 border-slate-100'
                        : 'text-white/60 hover:text-white border-white/10'
                    }`}
                  >
                    <Home className="w-4 h-4" />
                    Back to Krumago
                  </button>
                )}

                {/* CTA row */}
                <div className="pt-2 space-y-2">
                  <button
                    onClick={() => handleMobileNavClick("/contact")}
                    className="w-full flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-sm md:text-base"
                  >
                    <Sparkles className="w-4 h-4" />
                    Get Started
                  </button>

                  {!isAISidebarOpen && (
                    <button
                      onClick={() => { handleAIClick(); setIsMobileMenuOpen(false); }}
                      className={`w-full flex items-center justify-center space-x-2 px-3 md:px-4 py-2 md:py-2.5 font-semibold rounded-lg border transition-all duration-300 text-sm md:text-base ${
                        useWhiteStyle ? 'text-slate-700 border-slate-300' : 'text-white border-white'
                      }`}
                    >
                      <SmartToy className="w-4 h-4" />
                      <span>AI Assistant</span>
                    </button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <AISidebar open={isAISidebarOpen} onClose={handleAIClose} />
    </>
  );
};

export default KrumagoHeader;
